import type {
    FixerNameToSignature,
    FixerNameToVersion,
    FixerVersioningConfig,
    TagsAndExtras
} from '@wix/document-services-types'
import {ReportableError} from '../ReportableIssue'

const getFixerIndexFromSignature = (signature: string): number => {
    return Number(atob(signature).split('_')[0])
}

const getIndexesToFixerNames = (fixerNamesToSignatures: FixerNameToSignature): Record<number, string> => {
    const indexesToFixerNames: Record<number, string> = {}
    Object.keys(fixerNamesToSignatures).forEach(fixerName => {
        const index = getFixerIndexFromSignature(fixerNamesToSignatures[fixerName])
        indexesToFixerNames[index] = fixerName
    })
    return indexesToFixerNames
}

const getFixerNamesToIndexes = (fixerNamesToSignatures: FixerNameToSignature): Record<string, number> => {
    const fixerNamesToIndexes: Record<string, number> = {}
    Object.keys(fixerNamesToSignatures).forEach(fixerName => {
        const index = getFixerIndexFromSignature(fixerNamesToSignatures[fixerName])
        fixerNamesToIndexes[fixerName] = index
    })
    return fixerNamesToIndexes
}

const validateThatIndexesAreUnique = (fixerNamesToIndexes: Record<string, number>) => {
    const indexes = Object.values(fixerNamesToIndexes)
    const uniqueIndexes = new Set(indexes)
    if (uniqueIndexes.size !== indexes.length) {
        throw new Error('Fixer indexes must be unique')
    }
}

export const encodeFixerVersioning = (
    fixerNamesToVersions: FixerNameToVersion,
    fixerVersioningConfig: FixerVersioningConfig
): string => {
    const arrayOfFixersVersions = Array(fixerVersioningConfig.lastFixerIndex + 1).fill(0)
    const fixerNamesToIndexes = getFixerNamesToIndexes(fixerVersioningConfig.fixers)
    validateThatIndexesAreUnique(fixerNamesToIndexes)
    Object.keys(fixerNamesToVersions).forEach(fixerName => {
        const fixerIndex = fixerNamesToIndexes[fixerName]
        const fixerVersion = fixerNamesToVersions[fixerName]
        if (fixerIndex === undefined) {
            return
        }
        if (fixerVersion < 0 || fixerVersion > 255) {
            throw new Error(
                `Fixer version must be a number between 0 and 255, got ${fixerVersion} for fixer ${fixerName}`
            )
        }
        if (fixerIndex > fixerVersioningConfig.lastFixerIndex) {
            throw new Error(
                `Fixer index is not allowed to be greater than the last fixer index, got ${fixerIndex} for fixer ${fixerName}, while lastFixerIndex is ${fixerVersioningConfig.lastFixerIndex}`
            )
        }
        arrayOfFixersVersions[fixerIndex] = fixerVersion
    })
    return btoa(String.fromCharCode(...new Uint8Array(arrayOfFixersVersions)))
}

export const decodeFixerVersioning = (
    encodedFixerVersioning: string,
    fixerNamesToSignatures: FixerNameToSignature,
    captureError: (error: Error, op?: TagsAndExtras) => void
): FixerNameToVersion => {
    const safeAtob = (encodedString: string) => {
        try {
            return atob(encodedString)
        } catch (e) {
            captureError(
                new ReportableError({
                    message: 'Failed to decode fixer versioning',
                    errorType: 'decodeFixerVersioning',
                    extras: {encodedFixerVersioning: encodedString, error: e}
                })
            )
            return '' // This changes the function "signature", instead of throwing an exception that prevents the editor from loading, it will cause all fixers to re-run on the page document.
        }
    }
    const arrayOfFixersVersions = safeAtob(encodedFixerVersioning)
        .split('')
        .map(c => c.charCodeAt(0))

    const fixerNamesToVersions: FixerNameToVersion = {}
    const indexesToFixerNames = getIndexesToFixerNames(fixerNamesToSignatures)
    arrayOfFixersVersions.forEach((fixerVersion, index) => {
        if (fixerVersion === 0) {
            return
        }
        const fixerName = indexesToFixerNames[index]
        if (fixerName) {
            fixerNamesToVersions[fixerName] = fixerVersion
        }
    })

    return fixerNamesToVersions
}
